import classNames from 'classnames';
import isEmpty from 'lodash/isEmpty';
import { useState, useEffect, useMemo, type JSX } from 'react';

import { SelectOption } from '@/shared/types';

import { FormRow, FormElement, FormInputWrapper } from '@/components/old';

import { getCountrySelect, getRegionSelect, getDistrictSelect, getCitySelect, getStreetSelect } from '@/api/apiService';
import { DictionaryStreet, FormAddress, AddressFieldName } from '@/api/types';

import { FormConstants, IdConstants } from '../constants';
import { CorpsField } from '../Inputs/CorpsField';
import { FlatField } from '../Inputs/FlatField';
import { HouseField } from '../Inputs/HouseField';
import { InputField } from '../Inputs/InputField';
import { SettlementField } from '../Inputs/SettlementField';
import { ZipCodeField } from '../Inputs/ZipCodeField';
import InitialSelect from '../Select';
import { validateSettlement } from '../Validation';

type InputFieldName<T extends string> = `${T}_${AddressFieldName}`;

interface Props<T extends string> {
  title: string;
  name: T;
  stringifiedAddress: string;
  onlyUkraine?: boolean;
  includeZipCode?: boolean;
  handleAddressChange: (name: T, newAddress: FormAddress) => void;
  handleInputChange: (name: InputFieldName<T>, value: Nullable<'testValueForValidation'>) => void;
  handleClearInput: (name: InputFieldName<T>) => void;
}

export const AddressBlock = <T extends string>({
  title,
  name: stringifiedAddressFieldName,
  stringifiedAddress,
  onlyUkraine = false,
  includeZipCode = false,
  handleAddressChange,
  handleInputChange,
  handleClearInput,
}: Props<T>): JSX.Element => {
  const [optionsCountry, setOptionsCountry] = useState<SelectOption[]>([]);
  const [optionsRegion, setOptionsRegion] = useState<SelectOption[]>([]);
  const [optionsDistrict, setOptionsDistrict] = useState<SelectOption[]>([]);
  const [optionsCity, setOptionsCity] = useState<SelectOption[]>([]);
  const [optionsStreet, setOptionsStreet] = useState<
    Array<SelectOption & Record<'postCode', DictionaryStreet['postCode']>>
  >([]);

  const address = useMemo<FormAddress>(() => JSON.parse(stringifiedAddress || '{}'), [stringifiedAddress]);
  const isUkraineSelected = useMemo<boolean>(() => address.country?.id === IdConstants.UKRAINE, [address.country]);

  const isRegionDisabled = useMemo<boolean>(
    () => isEmpty(address.country) || !isUkraineSelected,
    [address.country, isUkraineSelected],
  );
  const isDistrictDisabled = useMemo<boolean>(
    () =>
      isEmpty(address.region) ||
      address.region?.id === IdConstants.KYIV ||
      address.region?.id === IdConstants.SEVASTOPOL,
    [address.region],
  );
  const isCityDisabled = useMemo<boolean>(
    () =>
      address[AddressFieldName.region]?.id !== IdConstants.KYIV &&
      address[AddressFieldName.region]?.id !== IdConstants.SEVASTOPOL &&
      isEmpty(address[AddressFieldName.district]) &&
      isUkraineSelected,
    [address, isUkraineSelected],
  );
  const isStreetDisabled = useMemo<boolean>(
    () => isEmpty(address.settlement) && isUkraineSelected,
    [address.settlement, isUkraineSelected],
  );

  useEffect(() => {
    (async (): Promise<void> => {
      if (isEmpty(address) || Object.values(address).every(isEmpty)) {
        const countries = await getCountrySelect();
        const UKRAINE = countries.find((country) => country.id === IdConstants.UKRAINE);

        handleChange(
          `${stringifiedAddressFieldName}_${AddressFieldName.country}`,
          AddressFieldName.country,
          UKRAINE as SelectOption,
        );
      } else {
        Object.entries(address).forEach(([addressFieldName, addressFieldValue]) => {
          handleInputChange(
            `${stringifiedAddressFieldName}_${addressFieldName as AddressFieldName}`,
            addressFieldValue ? 'testValueForValidation' : null,
          );
        });
      }
    })();
  }, []);

  const handleChange = <FieldName extends AddressFieldName>(
    inputFieldName: InputFieldName<T>,
    fieldName: FieldName,
    fieldValue: FormAddress[FieldName],
  ): void => {
    if (address[fieldName] !== fieldValue) {
      const newAddressValue = { ...address };

      switch (fieldName) {
        case AddressFieldName.country:
          newAddressValue[AddressFieldName.region] = null;
          handleClearInput(`${stringifiedAddressFieldName}_${AddressFieldName.region}`);

        case AddressFieldName.region:
          newAddressValue[AddressFieldName.district] = null;
          handleClearInput(`${stringifiedAddressFieldName}_${AddressFieldName.district}`);
          setOptionsDistrict([]);

        case AddressFieldName.district:
          newAddressValue[AddressFieldName.settlement] = null;
          handleClearInput(`${stringifiedAddressFieldName}_${AddressFieldName.settlement}`);
          setOptionsCity([]);

        case AddressFieldName.settlement:
          newAddressValue[AddressFieldName.street] = null;
          handleClearInput(`${stringifiedAddressFieldName}_${AddressFieldName.street}`);
          setOptionsStreet([]);

        case AddressFieldName.street:
          [AddressFieldName.house, AddressFieldName.building, AddressFieldName.apartment].forEach(
            (addressFieldName) => {
              newAddressValue[addressFieldName] = null;
              handleClearInput(`${stringifiedAddressFieldName}_${addressFieldName}`);
            },
          );

          newAddressValue[AddressFieldName.zip_code] = null;
          handleClearInput(`${stringifiedAddressFieldName}_${AddressFieldName.zip_code}`);

        default:
          newAddressValue[fieldName] = fieldValue;

          if (fieldName === AddressFieldName.region) {
            if (
              (fieldValue as SelectOption).id === IdConstants.KYIV ||
              (fieldValue as SelectOption).id === IdConstants.SEVASTOPOL
            ) {
              newAddressValue[AddressFieldName.settlement] = fieldValue;
              handleInputChange(
                `${stringifiedAddressFieldName}_${AddressFieldName.settlement}`,
                'testValueForValidation',
              );
            }
          }
          if (fieldName === AddressFieldName.street) {
            const correctlyTypedValue = fieldValue as FormAddress[AddressFieldName.street];

            if (includeZipCode && typeof correctlyTypedValue === 'object' && correctlyTypedValue?.postCode) {
              newAddressValue[AddressFieldName.zip_code] = correctlyTypedValue.postCode;
              handleInputChange(
                `${stringifiedAddressFieldName}_${AddressFieldName.zip_code}`,
                'testValueForValidation',
              );
            }
          }

          handleInputChange(inputFieldName, 'testValueForValidation');
          handleAddressChange(stringifiedAddressFieldName, newAddressValue);
          break;
      }
    }
  };

  return (
    <>
      <FormElement variant="title">{title}</FormElement>

      <FormRow>
        <FormInputWrapper
          label="Країна"
          required
          marginBottomNone
          className={classNames({ 'disabled-div': onlyUkraine })}
        >
          <div className="input-select js-input-select">
            <InitialSelect
              name={`${stringifiedAddressFieldName}_${AddressFieldName.country}`}
              options={optionsCountry}
              placeholder="Не вибрано"
              disabled={onlyUkraine}
              defaultValue={address.country}
              onChange={(newValue, name): void =>
                handleChange(name as InputFieldName<T>, AddressFieldName.country, newValue)
              }
              onMenuOpen={(): void => {
                if (!optionsCountry.length) {
                  getCountrySelect(setOptionsCountry);
                }
              }}
            />
          </div>
        </FormInputWrapper>

        <FormInputWrapper
          label="Область"
          required
          marginBottomNone
          className={classNames({ 'disabled-div': isRegionDisabled })}
        >
          <div className="input-select js-input-select">
            <InitialSelect
              name={`${stringifiedAddressFieldName}_${AddressFieldName.region}`}
              options={optionsRegion}
              placeholder="Не вибрано"
              defaultValue={address.region}
              disabled={isRegionDisabled}
              onChange={(newValue, name): void => {
                handleChange(name as InputFieldName<T>, AddressFieldName.region, newValue);
              }}
              onMenuOpen={(): void => {
                if (!optionsRegion.length) {
                  getRegionSelect(setOptionsRegion);
                }
              }}
            />
          </div>
        </FormInputWrapper>
      </FormRow>

      <FormRow>
        <FormInputWrapper
          label="Район"
          required
          marginBottomNone
          className={classNames({ 'disabled-div': isDistrictDisabled })}
        >
          <div className="input-select js-input-select">
            <InitialSelect
              name={`${stringifiedAddressFieldName}_${AddressFieldName.district}`}
              options={optionsDistrict}
              placeholder="Не вибрано"
              defaultValue={address.district}
              disabled={isDistrictDisabled}
              onChange={(newValue, name): void =>
                handleChange(name as InputFieldName<T>, AddressFieldName.district, newValue)
              }
              onMenuOpen={(): void => {
                if (!optionsDistrict.length && address.region?.id) {
                  getDistrictSelect(address.region.id, setOptionsDistrict);
                }
              }}
            />
          </div>
        </FormInputWrapper>

        <FormInputWrapper
          label={isUkraineSelected ? 'Назва населеного пункту' : undefined}
          required
          marginBottomNone
          className={classNames({ 'disabled-div': isCityDisabled })}
        >
          {isUkraineSelected && (
            <div className="input-select js-input-select">
              <InitialSelect
                name={`${stringifiedAddressFieldName}_${AddressFieldName.settlement}`}
                options={optionsCity}
                placeholder="Не вибрано"
                defaultValue={address.settlement as SelectOption}
                disabled={
                  address[AddressFieldName.region]?.id === IdConstants.KYIV ||
                  address[AddressFieldName.region]?.id === IdConstants.SEVASTOPOL ||
                  isEmpty(address[AddressFieldName.district])
                }
                onChange={(newValue, name): void =>
                  handleChange(name as InputFieldName<T>, AddressFieldName.settlement, newValue)
                }
                onMenuOpen={(): void => {
                  if (!optionsCity.length && address.district?.id) {
                    getCitySelect(address.district.id, setOptionsCity);
                  }
                }}
              />
            </div>
          )}

          {!isUkraineSelected && (
            <SettlementField
              name={`${stringifiedAddressFieldName}_${AddressFieldName.settlement}`}
              value={address.settlement as string}
              required
              change={(name, value): void =>
                handleChange(name as InputFieldName<T>, AddressFieldName.settlement, String(value).toUpperCase())
              }
            />
          )}
        </FormInputWrapper>
      </FormRow>

      {isUkraineSelected ? (
        <FormInputWrapper
          label="Назва вулиці"
          required
          marginBottomNone
          className={classNames({ 'disabled-div': isStreetDisabled })}
        >
          <div className="input-select js-input-select">
            <InitialSelect
              name={`${stringifiedAddressFieldName}_${AddressFieldName.street}`}
              options={optionsStreet}
              placeholder="Не вибрано"
              defaultValue={address.street as SelectOption}
              disabled={isStreetDisabled}
              onChange={(newValue, name): void => {
                handleChange(name as InputFieldName<T>, AddressFieldName.street, newValue);
              }}
              onMenuOpen={(): void => {
                if (!optionsStreet.length) {
                  getStreetSelect((address.settlement as SelectOption).id as string, setOptionsStreet);
                }
              }}
            />
          </div>
        </FormInputWrapper>
      ) : (
        <InputField
          required
          name={`${stringifiedAddressFieldName}_${AddressFieldName.street}`}
          value={address.street as string}
          change={(name, value): void => handleChange(name as InputFieldName<T>, AddressFieldName.street, value)}
          maxLength={FormConstants.INPUT_MAX_LENGTH}
          validator={validateSettlement}
          label="Назва вулиці"
        />
      )}

      <FormRow>
        <HouseField
          name={`${stringifiedAddressFieldName}_${AddressFieldName.house}`}
          value={address.house ?? ''}
          change={(name, value): void => handleChange(name as InputFieldName<T>, AddressFieldName.house, value)}
          required
        />
        <CorpsField
          name={`${stringifiedAddressFieldName}_${AddressFieldName.building}`}
          value={address.building ?? ''}
          change={(name, value): void => handleChange(name as InputFieldName<T>, AddressFieldName.building, value)}
        />
        <FlatField
          name={`${stringifiedAddressFieldName}_${AddressFieldName.apartment}`}
          value={address.apartment ?? ''}
          change={(name, value): void => handleChange(name as InputFieldName<T>, AddressFieldName.apartment, value)}
        />
      </FormRow>

      {includeZipCode && (
        <FormRow>
          <ZipCodeField
            name={`${stringifiedAddressFieldName}_${AddressFieldName.zip_code}`}
            value={address.zip_code ?? ''}
            change={(name, value): void => handleChange(name as InputFieldName<T>, AddressFieldName.zip_code, value)}
          />
        </FormRow>
      )}
    </>
  );
};
